/**
 * SSO TRACE LOGGER
 * 
 * Logger utilitario para rastrear el flujo completo de SSO handoff
 * con deduplicación para evitar logs repetidos en re-renders.
 * 
 * @package @shared/utils
 */

interface SsoTraceContext {
  app: string;
  feature: 'SSO_HANDOFF';
  traceId?: string;
}

interface SsoTrace {
  traceId: string;
  step: (name: string, data?: Record<string, any>) => void;
  warn: (name: string, data?: Record<string, any>) => void;
  error: (name: string, data?: Record<string, any>) => void;
  once: (key: string, fn: () => void) => void;
  maskToken: (token?: string) => string;
  maskCode: (code?: string) => string;
}

// Deduplicación global por sesión de ejecución
const printedKeys = new Set<string>();
const traceGroups = new Map<string, boolean>();

/**
 * Crea un trace único para un intento de SSO
 */
export function createSsoTrace(context: SsoTraceContext): SsoTrace {
  const traceId = context.traceId || generateTraceId();
  const groupKey = `SSO_TRACE_${traceId}_${context.app}`;
  
  // Crear grupo colapsado una sola vez
  if (!traceGroups.has(groupKey)) {
    traceGroups.set(groupKey, true);
    console.groupCollapsed(`🔐 SSO TRACE [${traceId}] [${context.app}]`);
  }
  
  let stepCounter = 0;
  
  const step = (name: string, data?: Record<string, any>) => {
    stepCounter++;
    const key = `${traceId}:${name}`;
    
    if (printedKeys.has(key)) {
      return; // Ya se imprimió
    }
    
    printedKeys.add(key);
    
    const sanitizedData = sanitizeData(data);
    console.log(
      `%c[${stepCounter}] ${name}`,
      'color: #3b82f6; font-weight: bold;',
      sanitizedData
    );
  };
  
  const warn = (name: string, data?: Record<string, any>) => {
    const key = `${traceId}:WARN:${name}`;
    
    if (printedKeys.has(key)) {
      return;
    }
    
    printedKeys.add(key);
    
    const sanitizedData = sanitizeData(data);
    console.warn(
      `%c⚠️ ${name}`,
      'color: #f59e0b; font-weight: bold;',
      sanitizedData
    );
  };
  
  const error = (name: string, data?: Record<string, any>) => {
    const key = `${traceId}:ERROR:${name}`;
    
    if (printedKeys.has(key)) {
      return;
    }
    
    printedKeys.add(key);
    
    const sanitizedData = sanitizeData(data);
    console.error(
      `%c❌ ${name}`,
      'color: #ef4444; font-weight: bold;',
      sanitizedData
    );
  };
  
  const once = (key: string, fn: () => void) => {
    const fullKey = `${traceId}:ONCE:${key}`;
    
    if (printedKeys.has(fullKey)) {
      return;
    }
    
    printedKeys.add(fullKey);
    fn();
  };
  
  const maskToken = (token?: string): string => {
    if (!token || token.length < 10) {
      return '***';
    }
    return `${token.substring(0, 6)}…${token.substring(token.length - 4)}`;
  };
  
  const maskCode = (code?: string): string => {
    if (!code || code.length < 6) {
      return '***';
    }
    return `${code.substring(0, 6)}…`;
  };
  
  return {
    traceId,
    step,
    warn,
    error,
    once,
    maskToken,
    maskCode,
  };
}

/**
 * Genera un traceId único
 */
function generateTraceId(): string {
  return `trace_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Sanitiza datos para logging (elimina tokens completos)
 */
function sanitizeData(data?: Record<string, any>): Record<string, any> | undefined {
  if (!data) return undefined;
  
  const sanitized: Record<string, any> = {};
  
  for (const [key, value] of Object.entries(data)) {
    const lowerKey = key.toLowerCase();
    
    // Omitir tokens completos
    if (
      lowerKey.includes('token') && 
      typeof value === 'string' && 
      value.length > 20
    ) {
      // Si es un token largo, maskearlo
      sanitized[key] = `${value.substring(0, 6)}…${value.substring(value.length - 4)}`;
    } else if (lowerKey.includes('session_json') || lowerKey.includes('session')) {
      // Omitir session_json completo, solo mostrar keys
      if (typeof value === 'object' && value !== null) {
        sanitized[key] = `{keys: ${Object.keys(value).join(', ')}}`;
      } else {
        sanitized[key] = '[REDACTED]';
      }
    } else if (typeof value === 'object' && value !== null) {
      // Recursivamente sanitizar objetos
      sanitized[key] = sanitizeData(value);
    } else {
      sanitized[key] = value;
    }
  }
  
  return sanitized;
}

/**
 * Limpia keys impresas (útil para testing o reset)
 */
export function clearSsoTraceCache(): void {
  printedKeys.clear();
  traceGroups.clear();
}
